import type { Temporal } from "./temporal";

import { parsePlainDate, parseOffsetInstant, parsePlainTime } from "./parsing";
import { clock } from "./temporal";

type CalendarValue =
  | Temporal.PlainDate
  | Temporal.ZonedDateTime
  | string
  | null
  | undefined;
type TimeValue =
  | Temporal.PlainTime
  | Temporal.PlainDateTime
  | Temporal.ZonedDateTime
  | null
  | undefined;

const pad = (value: number) => String(value).padStart(2, "0");
const relativeFormatter = new Intl.RelativeTimeFormat("ru-RU", {
  numeric: "always",
});
const dateValue = (value: CalendarValue) =>
  typeof value === "string"
    ? (parsePlainDate(value) ?? parseOffsetInstant(value))
    : value;

export function formatDate(value: CalendarValue): string {
  const date = dateValue(value);
  return date
    ? `${pad(date.day)}.${pad(date.month)}.${String(date.year).padStart(4, "0")}`
    : "—";
}

export function formatDayMonth(value: CalendarValue): string {
  const date = dateValue(value);
  return date ? `${pad(date.day)}.${pad(date.month)}` : "—";
}

export function formatLongDate(value: CalendarValue): string {
  const date = dateValue(value);
  if (!date) return "—";
  const plain = "toPlainDate" in date ? date.toPlainDate() : date;
  return plain.toLocaleString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function formatTime(input: TimeValue | string): string {
  const value =
    typeof input === "string"
      ? (parsePlainTime(input) ?? parseOffsetInstant(input))
      : input;
  return value ? `${pad(value.hour)}:${pad(value.minute)}` : "—";
}

export function formatDateTime(
  value: Temporal.ZonedDateTime | null | undefined,
): string {
  return value ? `${formatDate(value)} ${formatTime(value)}` : "—";
}

function withRelativeDateContext(
  value: Temporal.ZonedDateTime | null | undefined,
  now: Temporal.Instant,
  render: (
    date: Temporal.PlainDate,
    today: Temporal.PlainDate,
    value: Temporal.ZonedDateTime,
  ) => string,
): string {
  if (!value) return "—";
  return render(
    value.toPlainDate(),
    now.toZonedDateTimeISO(value.timeZoneId).toPlainDate(),
    value,
  );
}

/** Сегодня/вчера с точным временем; остальные дни — полная дата со временем. */
export function formatRelativeDateTime(
  value: Temporal.ZonedDateTime | null | undefined,
  now: Temporal.Instant = clock.now(),
): string {
  return withRelativeDateContext(value, now, (date, today) => {
    if (date.equals(today)) return `Сегодня в ${formatTime(value)}`;
    if (date.equals(today.subtract({ days: 1 })))
      return `Вчера в ${formatTime(value)}`;

    return formatDateTime(value);
  });
}

/** Компактная дата как в Telegram Android, в часовом поясе значения. */
export function formatCompactDateTime(
  value: Temporal.ZonedDateTime | null | undefined,
  now: Temporal.Instant = clock.now(),
): string {
  return withRelativeDateContext(value, now, (date, today, dateTime) => {
    const diffDays = date.until(today).days;
    const elapsedHours = dateTime.toInstant().until(now).total("hours");

    if (Math.abs(elapsedHours) >= 365 * 24) {
      return date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
    }

    // Вчерашние сообщения и события младше 8 часов сохраняют время.
    if (diffDays === 0 || (diffDays === 1 && elapsedHours < 8))
      return formatTime(dateTime);

    return date.toLocaleString(
      "ru-RU",
      diffDays > 0 && diffDays < 7
        ? { weekday: "short" }
        : { day: "2-digit", month: "short" },
    );
  });
}

export function formatRelativeDate(
  input: string | Temporal.ZonedDateTime | null | undefined,
  now: Temporal.Instant = clock.now(),
): string {
  const value = typeof input === "string" ? parseOffsetInstant(input) : input;
  if (!value) return "—";
  const date = value.toPlainDate();
  const today = now.toZonedDateTimeISO(value.timeZoneId).toPlainDate();
  if (date.equals(today)) return "сегодня";
  if (date.equals(today.subtract({ days: 1 }))) return "вчера";
  const label = date.toLocaleString("ru-RU", { day: "numeric", month: "long" });
  return date.year === today.year ? label : `${label} ${date.year}`;
}

/** Относительное время с календарными годами/месяцами в зоне отображения. */
export function formatTimeAgo(
  value: Temporal.ZonedDateTime | null | undefined,
  now: Temporal.Instant = clock.now(),
): string {
  if (!value) return "—";
  const duration = value.until(now.toZonedDateTimeISO(value.timeZoneId), {
    largestUnit: "years",
  });
  const units = [
    "years",
    "months",
    "days",
    "hours",
    "minutes",
    "seconds",
  ] as const;
  const unit = units.find((key) => Math.abs(duration[key]) >= 1) ?? "seconds";
  return relativeFormatter.format(-duration[unit], unit);
}
